import { resolveCategoryLabel } from '@/utils/market-category-label';
import { getBinaryOutcomeDisplayLabels } from '@/utils/market-variant';
import { mapFeedCards as mapFeedCardsDomain } from '@/domain/market/feed-card-model.js';
import { resolveFeedListDisplayCategoryKey } from '@/domain/market/feed-list-category.js';
import { resolveFeedCardShowUpdownFlow } from '@/domain/market/updown-flow-match.js';

/** 供 i18n 判定用的 outcomes 字段（API 原串或已解析数组） */
export function resolveOutcomesField(entity) {
  if (entity?.outcomesRaw != null && String(entity.outcomesRaw).trim() !== '') {
    return entity.outcomesRaw;
  }
  if (Array.isArray(entity?.outcomeValues) && entity.outcomeValues.length >= 2) {
    return entity.outcomeValues;
  }
  return null;
}

export function localizeBinaryLabels(entity, t) {
  const field = resolveOutcomesField(entity);
  if (!field) {
    return { yesLabel: entity?.yesLabel, noLabel: entity?.noLabel };
  }
  return getBinaryOutcomeDisplayLabels(field, t);
}

/** 单张卡片 i18n：category、Yes/No/Over/Under、marketRows 按钮文案 */
export function localizeFeedCard(card, t, te, options = {}) {
  const sourceCategoryKey = String(card.categoryKey ?? card.category ?? '').trim();
  const categoryKey = options.remapHomeListCategory
    ? resolveFeedListDisplayCategoryKey(sourceCategoryKey)
    : sourceCategoryKey;
  const category = resolveCategoryLabel(categoryKey, t, te) || categoryKey;

  let yesLabel = card.yesLabel;
  let noLabel = card.noLabel;
  if (card.variant === 'binary' || card.marketRows?.length) {
    const labels = localizeBinaryLabels(card, t);
    yesLabel = labels.yesLabel;
    noLabel = labels.noLabel;
  }

  let marketRows = card.marketRows;
  if (marketRows?.length) {
    marketRows = marketRows.map((row) => {
      const labels = localizeBinaryLabels(row, t);
      return {
        ...row,
        yesLabel: labels.yesLabel ?? row.yesLabel,
        noLabel: labels.noLabel ?? row.noLabel,
      };
    });
  }

  return {
    ...card,
    categoryKey,
    category,
    yesLabel,
    noLabel,
    marketRows,
    completeLabel: t('explore.complete'),
    showUpdownFlow: resolveFeedCardShowUpdownFlow(card),
  };
}

export function localizeFeedCards(cards, t, te, options = {}) {
  return cards.map((card) => localizeFeedCard(card, t, te, options));
}

/** Presentation 入口：domain 映射 → 本地化；options.locale 影响 PM 短标月日 */
export function mapFeedCardsForPresentation(apiRows, t, te, options = {}) {
  const { remapHomeListCategory, locale, ...domainOptions } = options;
  const localizeOptions = remapHomeListCategory ? { remapHomeListCategory: true } : {};
  return localizeFeedCards(
    mapFeedCardsDomain(apiRows, { ...domainOptions, locale }),
    t,
    te,
    localizeOptions
  );
}

/** FeedCardViewModel → MarketCard props（card 应已 localize） */
export function feedCardToMarketCardProps(card, t, te) {
  const categoryKey = String(card.categoryKey ?? '').trim();
  const category = card.category || resolveCategoryLabel(categoryKey, t, te) || categoryKey;

  const binaryLabels =
    card.variant === 'binary' ? localizeBinaryLabels(card, t) : { yesLabel: null, noLabel: null };

  const base = {
    marketId: card.id,
    orderSymbol: card.orderSymbol,
    imageUrl: card.imageUrl || '',
    completeLabel: card.completeLabel || t('explore.complete'),
    yesLabel: binaryLabels.yesLabel || card.yesLabel || t('explore.yes'),
    noLabel: binaryLabels.noLabel || card.noLabel || t('explore.no'),
    title: card.title,
    titleSubtitle: card.titleSubtitle || '',
    liquidityValue: card.volume || card.liquidityValue || '',
    category,
    outcomeValues: card.outcomeValues || [],
    footerTag: card.footerTag || '',
    footerTagVariant: card.footerTagVariant || '',
    showUpdownFlow: resolveFeedCardShowUpdownFlow(card),
    tradingCollateral: card.tradingCollateral || '',
    exchangeAddress: card.exchangeAddress || '',
    marketRows: (card.marketRows || []).map((row) => {
      const labels = localizeBinaryLabels(row, t);
      return {
        ...row,
        yesLabel: labels.yesLabel ?? row.yesLabel,
        noLabel: labels.noLabel ?? row.noLabel,
      };
    }),
    multiMarketRows: card.multiMarketRows || [],
  };

  if (card.variant === 'complete') {
    return {
      ...base,
      variant: 'complete',
      multiOptions: [],
      multiMarketRows: [],
      yesPercent: 0,
      noPercent: 0,
    };
  }

  if (card.variant === 'empty') {
    return {
      ...base,
      variant: 'empty',
      multiOptions: [],
      multiMarketRows: [],
      yesPercent: 0,
      noPercent: 0,
    };
  }

  if (card.variant === 'multi') {
    return {
      ...base,
      variant: 'multi',
      multiOptions: card.multiOptions || [],
      multiMarketRows: card.multiMarketRows || [],
      outcomeValues: card.outcomeValues || [],
      yesPercent: 0,
      noPercent: 0,
    };
  }

  return {
    ...base,
    variant: 'binary',
    multiOptions: [],
    multiMarketRows: card.multiMarketRows || [],
    yesPercent: card.yesPercent ?? 50,
    noPercent: card.noPercent ?? 50,
  };
}
